import {
  AuthenticatedAppGate,
  AuthLaunchDarklyProvider,
  AuthProvider,
  createCurrentUserProvisionValidator,
  createLoginMethodLookup,
  useAuth,
} from "@assetwatch/auth";
import {
  Alert,
  AssetWatchUiProvider,
  Container,
  Stack,
  Title,
} from "@assetwatch/ui";
import * as Sentry from "@sentry/react";
import { type QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";
import { AppFeatureGate } from "../components/AppFeatureGate";
import { AppNotFound } from "../components/AppNotFound";
import config from "../config";

const authApiBaseUrl =
  (import.meta.env.VITE_AUTH_API_BASE_URL as string | undefined)?.replace(
    /\/$/,
    "",
  ) ?? "";
const hasProvisioningApi = authApiBaseUrl.length > 0;

const validateAuthenticatedUser = hasProvisioningApi
  ? createCurrentUserProvisionValidator({})
  : undefined;
const signOutRedirect = {
  clientId: config.cognito.APP_CLIENT_ID,
  domain: config.cognito.DOMAIN,
  redirectUri: `${window.location.origin}/`,
};

const lookupLoginMethod = createLoginMethodLookup(authApiBaseUrl);

function AuthGate({ children }: { children: ReactNode }) {
  return (
    <AuthenticatedAppGate
      loadingFallback={
        <Container size="sm">
          <Stack gap={16}>
            <Title size="lg">Loading Field Ops</Title>
            <Alert tone="informative" title="Checking your session..." />
          </Stack>
        </Container>
      }
      lookupLoginMethod={lookupLoginMethod}
    >
      {children}
    </AuthenticatedAppGate>
  );
}

function SentryUserScopeSync() {
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.sub, email: user.email });
      return;
    }

    Sentry.setUser(null);
  }, [user]);

  return null;
}

function RootProviders() {
  return (
    <QueryClientProvider client={Route.useRouteContext().queryClient}>
      <AuthProvider
        signOutRedirect={signOutRedirect}
        validateAuthenticatedUser={validateAuthenticatedUser}
      >
        <AuthLaunchDarklyProvider
          clientSideID={import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID}
        >
          <SentryUserScopeSync />
          <AssetWatchUiProvider
            defaultTheme="light"
            storageKey="assetwatch-fieldops-theme"
          >
            <AuthGate>
              <AppFeatureGate>
                <Outlet />
              </AppFeatureGate>
            </AuthGate>
          </AssetWatchUiProvider>
        </AuthLaunchDarklyProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

function RootError({ error }: { error: Error }) {
  return (
    <Container size="sm">
      <Stack gap={16}>
        <Title size="lg">Unable to load Field Ops</Title>
        <Alert tone="negative" title={error.message} />
      </Stack>
    </Container>
  );
}

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  component: RootProviders,
  errorComponent: RootError,
  notFoundComponent: AppNotFound,
  pendingComponent: () => (
    <Container size="sm">
      <Stack gap={16}>
        <Title size="lg">Loading Field Ops</Title>
        <Alert tone="informative" title="Loading app view..." />
      </Stack>
    </Container>
  ),
});
